import { ref, computed, watch } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";

interface CityCoverage {
  city: string;
  region: string;
  isCovered: string;
}

interface FormType {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  values: Record<string, any>;
  touched: Record<string, boolean>;
  validateField?: (field: string) => void;
}

interface FieldType {
  model: string;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// Internal helpers for normalizing and parsing city/region
function findCityData(city: string, citiesCoverage: CityCoverage[]) {
  return citiesCoverage.find(
    (item) => item.city.trim().toUpperCase() === city.trim().toUpperCase()
  );
}

function parseCombinedCityRegion(value: string) {
  const match = value.match(/^(.*) \((.*)\)$/);
  if (match) {
    const [, city, region] = match;
    return { city, region };
  }
  return { city: value, region: "" };
}

export function useCityField(
  form: FormType,
  citiesCoverage: CityCoverage[] = []
): {
  cityError: typeof cityError;
  cityValidationPassed: typeof cityValidationPassed;
  searchText: typeof searchText;
  isValidCity: () => void;
  combinedCityRegion: typeof combinedCityRegion;
  getFilteredCities: () => Array<{
    label: string;
    value: string;
    isCovered: string;
  }>;
  getModelValue: (field: FieldType) => string;
  setModelValue: (field: FieldType, value: string) => void;
  selectedOption: typeof selectedOption;
} {
  const { t } = useI18n();
  const cityError = ref<string | null>(null);
  const cityValidationPassed = ref<boolean>(false);
  const searchText = ref("");
  const selectedOption = ref<{
    label: string;
    value: string;
    isCovered: string;
  } | null>(null);

  // City validation
  function isValidCity() {
    const cityValue = form.values.cityField?.trim() || "";
    if (!cityValue) {
      cityError.value = t("PAYJOY_CARD_VIEWS.FORM_CITY_REQUIRED_FIELD");
      cityValidationPassed.value = false;
      return;
    }
    const cityData = findCityData(cityValue, citiesCoverage);
    if (!cityData) {
      cityError.value = t(
        "PAYJOY_CARD_VIEWS.FORM_CITY_REGION_MUST_BE_SELECTED"
      );
      cityValidationPassed.value = false;
      return;
    }
    if (cityData.isCovered !== "true") {
      cityError.value = t("PAYJOY_CARD_VIEWS.ADDRESS_FORM_NO_COVERAGE");
      cityValidationPassed.value = false;
      return;
    }
    cityError.value = null;
    cityValidationPassed.value = true;
  }

  // Computed property for city + region
  const combinedCityRegion = computed({
    get: () => {
      const city = form.values.cityField || "";
      const region = form.values.regionField || "";
      return city && region ? `${city} (${region})` : city;
    },
    set: (value: string) => {
      const { city, region } = parseCombinedCityRegion(value);
      form.values.cityField = city;
      form.values.regionField = region;
    },
  });

  // Filtered options for autocomplete, including coverage info
  function getFilteredCities() {
    const cityFieldValue = searchText.value.toLowerCase() || "";
    const allOptions = citiesCoverage.map((item) => ({
      label: `${item.city} (${item.region})`,
      value: item.city,
      isCovered: item.isCovered,
    }));
    let filtered = allOptions;
    if (cityFieldValue) {
      filtered = allOptions.filter((opt) =>
        opt.label.toLowerCase().includes(cityFieldValue)
      );
    }
    // Remove duplicates by label
    const unique = Array.from(
      new Map(filtered.map((opt) => [opt.label, opt])).values()
    );
    return unique;
  }

  // Synchronize city/region when the field changes
  watch(
    () => form.values.cityField,
    (newVal) => {
      if (!newVal) {
        combinedCityRegion.value = "";
        form.values.regionField = "";
        selectedOption.value = null;
        cityValidationPassed.value = false;
        isValidCity();
        return;
      }
      const cityData = findCityData(newVal, citiesCoverage);
      if (cityData) {
        combinedCityRegion.value = `${cityData.city} (${cityData.region})`;
        form.values.cityField = cityData.city;
        form.values.regionField = cityData.region;
        selectedOption.value = {
          label: `${cityData.city} (${cityData.region})`,
          value: cityData.city,
          isCovered: cityData.isCovered,
        };
        isValidCity();
      } else {
        const { city, region } = parseCombinedCityRegion(newVal);
        combinedCityRegion.value = newVal;
        form.values.cityField = city;
        form.values.regionField = region;
        selectedOption.value = null;
        cityValidationPassed.value = false;
        isValidCity();
      }
    }
  );

  // Autocomplete methods
  function getModelValue(field: FieldType) {
    const fieldName = field.model || field.name;
    if (fieldName === "combinedCityRegion") {
      return combinedCityRegion.value;
    }
    return form.values[fieldName] ?? "";
  }

  function setModelValue(field: FieldType, value: string) {
    type OptionType = { label: string; value: string; isCovered: string };
    let option: OptionType | null = null;
    const fieldName = field.model || field.name;

    // Find the selected option in autocomplete (by label or value)
    const filtered = getFilteredCities();
    option =
      filtered.find((opt: OptionType) => {
        // Match by label, value, or if the value is inside the label (for manual typing)
        return (
          opt.label === value ||
          opt.value === value ||
          opt.label.toLowerCase().includes(value.toLowerCase())
        );
      }) || null;

    selectedOption.value = option;

    if (fieldName === "combinedCityRegion") {
      const { city, region } = parseCombinedCityRegion(value);
      form.values.cityField = city;
      form.values.regionField = region;
      searchText.value = city;
      form.touched.cityField = true;
      if (form.validateField) form.validateField("cityField");
    } else {
      // If we found a matching option, extract city and region from it
      if (option && fieldName === "cityField") {
        const { city, region } = parseCombinedCityRegion(option.label);
        form.values.cityField = city;
        form.values.regionField = region;
        searchText.value = city;
      } else {
        form.values[fieldName] = value;
      }

      if (fieldName === "cityField") {
        form.touched.cityField = true;
        if (form.validateField) form.validateField("cityField");
        if (!option) {
          searchText.value = value;
        }
      }
    }
    // Always validate city after selection or typing
    isValidCity();
  }

  return {
    cityError,
    cityValidationPassed,
    searchText,
    isValidCity,
    combinedCityRegion,
    getFilteredCities,
    getModelValue,
    setModelValue,
    selectedOption,
  };
}
